import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Divider,
} from '@mui/material';
import { demosService } from '../../services/demos';

const DemoStatusUpdateDialog = ({ demo, onUpdate, onCancel }) => {
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [outcome, setOutcome] = useState('');
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const demoLeads = useMemo(() => {
    if (demo?.leads && Array.isArray(demo.leads) && demo.leads.length > 0) {
      return demo.leads;
    }
    if (demo?.lead_id) {
      return [{
        id: demo.lead_id,
        first_name: demo.lead_first_name,
        last_name: demo.lead_last_name,
        attendance_status: 'invited',
      }];
    }
    return [];
  }, [demo]);

  const statusLabels = {
    scheduled: 'Scheduled',
    confirmed: 'Confirmed',
    completed: 'Completed',
    cancelled: 'Cancelled',
    rescheduled: 'Rescheduled',
  };

  const statusColors = {
    scheduled: 'primary',
    confirmed: 'info',
    completed: 'success',
    cancelled: 'error',
    rescheduled: 'warning',
  };

  useEffect(() => {
    if (demo) {
      setStatus(demo.status);
      setNotes(demo.notes || '');
      setOutcome(demo.outcome || '');
      setError('');
      const initial = {};
      demoLeads.forEach((lead) => {
        const current = lead.attendance_status;
        if (current === 'attended' || current === 'no_show') {
          initial[lead.id] = current;
        } else {
          initial[lead.id] = 'attended';
        }
      });
      setAttendance(initial);
    }
  }, [demo, demoLeads]);

  const handleSubmit = async () => {
    if (!status) {
      setError('Please select a status');
      return;
    }
    if (loading) return;

    if (status === 'completed' && demoLeads.length > 0) {
      const missing = demoLeads.filter((lead) => !attendance[lead.id]);
      if (missing.length > 0) {
        setError('Mark attendance for each lead before completing the demo');
        return;
      }
    }

    try {
      setLoading(true);
      setError('');

      const updateData = { status };
      if (notes?.trim()) updateData.notes = notes.trim();
      if (outcome?.trim()) updateData.outcome = outcome.trim();

      if (status === 'completed' && demoLeads.length > 0) {
        updateData.lead_attendance = demoLeads.map((lead) => ({
          lead_id: lead.id,
          attendance_status: attendance[lead.id],
        }));
      }

      await demosService.updateStatus(demo.id, updateData);
      onUpdate();
    } catch (err) {
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        setError(err.response.data.errors.join(', '));
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to update demo status');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) onCancel();
  };

  if (!demo) return null;

  const leadLabel = demoLeads.length === 1
    ? `${demoLeads[0].first_name || ''} ${demoLeads[0].last_name || ''}`.trim()
    : `${demoLeads.length} leads`;

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Update Demo Status
        <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1 }}>
          {leadLabel || demo.lead_email || 'Demo'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Typography variant="body2" color="textSecondary">Current Status:</Typography>
              <Chip
                label={statusLabels[demo.status] || demo.status}
                color={statusColors[demo.status] || 'default'}
                size="small"
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>New Status</InputLabel>
              <Select value={status} onChange={(e) => setStatus(e.target.value)} label="New Status">
                {Object.entries(statusLabels).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    <Chip label={label} color={statusColors[value]} size="small" variant="outlined" />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {status === 'completed' && demoLeads.length > 0 && (
            <Grid item xs={12}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="subtitle2" gutterBottom>
                Lead attendance
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Mark who attended and who did not show up.
              </Typography>
              {demoLeads.map((lead) => (
                <Box key={lead.id} sx={{ mb: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    {[lead.first_name, lead.last_name].filter(Boolean).join(' ') || lead.id}
                  </Typography>
                  <RadioGroup
                    row
                    value={attendance[lead.id] || 'attended'}
                    onChange={(e) => setAttendance((prev) => ({ ...prev, [lead.id]: e.target.value }))}
                  >
                    <FormControlLabel value="attended" control={<Radio size="small" />} label="Attended" />
                    <FormControlLabel value="no_show" control={<Radio size="small" />} label="No show" />
                  </RadioGroup>
                </Box>
              ))}
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes (Optional)"
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this status change..."
            />
          </Grid>

          {status === 'completed' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Demo Outcome (Optional)"
                multiline
                rows={3}
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
                placeholder="Describe the outcome of the demo..."
                helperText="This field is particularly useful for completed demos"
              />
            </Grid>
          )}

          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !status}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Updating...' : 'Update Status'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DemoStatusUpdateDialog;
